/**
 * @file Multimedia.js
 * @description Multimedia container component for displaying images, videos, and rich text content with optional navigation button.
 */

import { useState, useEffect, useContext } from 'react';
import './Multimedia.css';
import { useEditor, EditorContent } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import {
    Color,
    FontFamily,
    FontSize,
    TextStyle,
} from '@tiptap/extension-text-style';
import { ListItem } from '@tiptap/extension-list';
import { Placeholder } from '@tiptap/extensions';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import {
    Table,
    TableCell,
    TableHeader,
    TableRow,
} from '@tiptap/extension-table';
import ImageCarousel from './ImageCarousel';
import Video from '../video-component/Video';
import Emoji from '../../utils/tiptap/EmojiExtension';
import { fetchAssetByID } from '../../services/clientService';
import { getFileCategory } from '../helpers/FileUtils';
import ResizableImage from '../../utils/tiptap/ResizableImageExtension';
import { VideoNode } from '../new-portal-page/VideoNode';
import Highlight from '@tiptap/extension-highlight';
import { QRCodeNode } from '../new-portal-page/QRCodeNode';
import DivParagraph from '../new-portal-page/DivParagraph';
import { useAuthContext } from '../../store/Contexts';
import { fetchClientSSOById } from '../../services/ssoService';
import NotFoundBox from '../ui/no_data_indicators/NotFoundBox';
import { PortalLinkNode } from '../new-portal-page/PortalLinkNode';
import { useMenuLinkNavigation } from '../../hooks/useMenuLinkNavigation';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import { getText } from '../../i18n';

function normalizeHtmlForEditorLoad(html) {
    if (typeof html !== 'string' || !html) {
        return html || '';
    }
    return html.replace(/<div>\s*<br\s*\/?>\s*<\/div>/gi, '<div></div>');
}

function isAssetAnchor(anchor) {
    if (!anchor) {
        return false;
    }

    if (anchor.getAttribute('data-link-type') === 'asset') {
        return true;
    }

    const href = anchor.getAttribute('href') || '';
    return /\/files\/asset\//i.test(href);
}

const CustomTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            backgroundColor: {
                default: null,
                parseHTML: element =>
                    element.getAttribute('data-background-color'),
                renderHTML: attributes => {
                    return {
                        'data-background-color': attributes.backgroundColor,
                        style: `background-color: ${attributes.backgroundColor}`,
                    };
                },
            },
        };
    },
});

function Multimedia({
    url,
    caption,
    containerAssets,
    id,
    name,
    portalId,
    navigationStack,
    editMode = false,
    isPreview = false,
    isViewLive = false,
    onShowPortalView,
    button,
    handleSetBackgroundColor,
    tenantSSOId,
    onLinkClick,
    backgroundColor,
}) {
    const [images, setImages] = useState([]);
    const [videoUrl, setVideoUrl] = useState(url);
    const [showSSONotFound, setShowSSONotFound] = useState(false);
    const { publicBrand } = useAuthContext();
    const { showModal } = useContext(ModalContext);
    const isInteractive = !editMode;

    const editor = useEditor({
        extensions: [
            Color.configure({ types: [TextStyle.name, ListItem.name] }),
            TextStyle.configure({ types: [ListItem.name] }),
            FontFamily.configure({
                types: ['textStyle'],
            }),
            FontSize,
            PortalLinkNode.configure(
                editMode
                    ? {
                          interactive: false,
                          onBlockedClick: null,
                      }
                    : isPreview && typeof onShowPortalView === 'function'
                      ? {
                            interactive: false,
                            onBlockedClick: portalIdValue => {
                                onShowPortalView(
                                    Number(portalIdValue),
                                    navigationStack,
                                );
                            },
                        }
                      : {
                            interactive: true,
                            navigationStack: navigationStack,
                            parentPortalId: portalId,
                            ...(isViewLive && { routePrefix: '/view-live' }),
                        },
            ),
            StarterKit.configure({
                paragraph: false,
                // StarterKit v3 bundles these; keep the explicitly configured ones.
                link: false,
                underline: false,
                trailingNode: false,
                bulletList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
                orderedList: {
                    keepMarks: true,
                    keepAttributes: false,
                },
            }),
            Placeholder.configure({
                placeholder: getText('msg_8b4539a50a3b'),
            }),
            Underline,
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                protocols: ['ftp', 'mailto'],
                HTMLAttributes: {
                    class: '',
                },
            }),
            ResizableImage,
            Table.configure({
                resizable: true,
            }),
            TableRow,
            TableHeader,
            TableCell,
            CustomTableCell,
            Emoji,
            VideoNode,
            QRCodeNode,
            DivParagraph,
        ],
        content: normalizeHtmlForEditorLoad(caption),
        editable: false,
    });

    useEffect(() => {
        async function getClientSSO() {
            try {
                const data = await fetchClientSSOById(tenantSSOId);
                const shouldShowSSONotFound = data.status !== 'Live';
                setShowSSONotFound(shouldShowSSONotFound);

                if (!handleSetBackgroundColor) {
                    return;
                }

                if (shouldShowSSONotFound) {
                    handleSetBackgroundColor('#FFFFFF');
                    return;
                }

                handleSetBackgroundColor(backgroundColor);
            } catch (error) {
                console.error('Could not fetch client SSO by Id: ', error);
                setShowSSONotFound(true);
                if (handleSetBackgroundColor) {
                    handleSetBackgroundColor('#FFFFFF');
                }
            }
        }

        if (tenantSSOId) {
            getClientSSO();
            return;
        }

        if (handleSetBackgroundColor) {
            handleSetBackgroundColor(backgroundColor);
        }
    }, [tenantSSOId, backgroundColor, handleSetBackgroundColor]);

    useEffect(() => {
        async function fetchAssets() {
            const imageURLs = [];

            const orderedContainerAssets = [...containerAssets].sort(
                (a, b) => a.assetOrder - b.assetOrder,
            );

            for (const containerAsset of orderedContainerAssets) {
                if (!containerAsset.assetId) {
                    continue;
                }

                const fetchedAsset = await fetchAssetByID(
                    containerAsset.assetId,
                    null,
                );
                containerAsset.url = fetchedAsset.url;

                if (caption.indexOf(containerAsset.url) !== -1) {
                    continue;
                }

                const fileCategory = getFileCategory(containerAsset.url);

                if (fileCategory === 'video') {
                    setVideoUrl(containerAsset.url);
                    continue;
                }

                if (fileCategory === 'image') {
                    imageURLs.push(containerAsset.url);
                }
            }

            setImages(imageURLs);
        }

        fetchAssets();
    }, [containerAssets, caption]);

    useEffect(() => {
        if (!editor) {
            return;
        }

        if (editor.getHTML() === caption) {
            return;
        }

        const normalizedCaption = normalizeHtmlForEditorLoad(caption);
        editor.commands.setContent(normalizedCaption, { emitUpdate: false });
    }, [caption, editor]);

    useEffect(() => {
        setVideoUrl(url);
    }, [url]);

    useMenuLinkNavigation(editor, { isPreview, isViewLive });

    function handleContentClickCapture(event) {
        if (editMode) {
            return;
        }

        const assetAnchor = event.target.closest('a');

        if (!isAssetAnchor(assetAnchor)) {
            return;
        }

        const href = assetAnchor.getAttribute('href');
        if (!href) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        showModal({
            type: ModalTypes.ASSET_VIEW,
            title: assetAnchor.textContent?.trim() || 'Asset Preview',
            text: href,
        });
    }

    function handleButtonClick(e) {
        e.stopPropagation();
        if (onLinkClick) {
            onLinkClick();
        }
    }

    if (!editor) {
        return null;
    }

    const hasCaption =
        caption !== '' &&
        caption !== '<div><br></div>' &&
        caption !== '<div></div>';
    const hasImages = images && images.length > 0;
    const hasVideo = videoUrl && videoUrl !== '';
    const hasButton = button && button !== '';
    const hasMedia = hasImages || hasVideo;
    const isMediaOnly = hasMedia && !hasCaption && !hasButton;

    if (showSSONotFound) {
        return (
            <NotFoundBox
                backgroundColor="#f0f4ff"
                title={getText('msg_7cc6a6e8c21a')}
                description={getText('msg_8bd86c93ba96')}
                width="100%"
            />
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: isMediaOnly ? '0px' : '20px',
                width: '100%',
                height: isMediaOnly ? '100%' : 'auto',
                marginTop: isMediaOnly ? '0' : 'auto',
                marginBottom: isMediaOnly ? '0' : 'auto',
                verticalAlign: 'middle',
                padding: '0',
                overflow: isMediaOnly ? 'hidden' : 'visible',
            }}
        >
            {hasImages && (
                <ImageCarousel
                    images={images}
                    fill={isMediaOnly}
                    editMode={editMode}
                />
            )}

            {hasVideo && (
                <div
                    style={isMediaOnly ? { width: '100%', height: '100%' } : {}}
                >
                    <Video
                        url={videoUrl}
                        caption=""
                        id={id}
                        name={name}
                        portalId={portalId}
                        isInteractive={isInteractive}
                        isViewLive={isViewLive}
                        fill={isMediaOnly}
                    />
                </div>
            )}

            {hasCaption && editor && (
                <div
                    style={{
                        margin: hasMedia ? '0 20px' : '0px',
                    }}
                    onClickCapture={handleContentClickCapture}
                >
                    <EditorContent
                        editor={editor}
                        className={
                            editMode
                                ? 'custom-readonly-editor disable-links'
                                : 'custom-readonly-editor'
                        }
                    />
                </div>
            )}
            {hasButton && (
                <div
                    className="navigation-button button-base button-primary"
                    style={{
                        backgroundColor: publicBrand?.accessoryColor,
                        color: publicBrand?.accessoryTextColor,
                        cursor: 'pointer',
                        margin: hasMedia ? '0 20px' : '0px',
                    }}
                    onClick={handleButtonClick}
                    role="button"
                    tabIndex={0}
                >
                    {button}
                </div>
            )}
        </div>
    );
}

export default Multimedia;
